import React, { useState } from 'react';
import { SubcampaignInfluencerProfile } from 'types';
import { FullEntertainmentVideo } from 'modules/entertainment-videos/types';
import ConfirmModal from 'components/modal/confirm-modal/ConfirmModal';
import { __t, applyTranslations } from 'utils/language';
import { setIsEditMode, unpublishEntertainmentVideo, updateEntertainmentVideo } from '../../reducers';
import { EntertainmentVideoEditForm } from '../entertainment-video-edit-form';
import { EntertainmentVideoReviewDetails } from '../entertainment-video-review-details';
import { EntertainmentVideoReviewHeader } from '../entertainment-video-review-header';
import { EntertainmentVideoReviewInfo } from '../entertainment-video-review-info';
import { useEntertainmentVideosForReview } from './hooks';
import './entertainment-video-review.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  isEditModeSelector,
  isUnpublishingSelector,
  isUpdatingSelector
} from 'selectors/entertainment-video';

applyTranslations(require('./entertainment-video-review.i18n.json'));

function translate(path: string) {
  return __t(`entertainmentVideoReview${path}`);
}

export function EntertainmentVideoReview({
  pageType,
  deliverableId,
  offerId,
  influencerProfile,
  initialEntertainmentVideo
}: {
  pageType: 'offer' | 'content',
  deliverableId: number,
  offerId: number,
  influencerProfile: SubcampaignInfluencerProfile,
  initialEntertainmentVideo: FullEntertainmentVideo
}) {
  const dispatch = useDispatch();

  const isEditMode = useSelector(isEditModeSelector);
  const isUpdating = useSelector(isUpdatingSelector);
  const isUnpublishing = useSelector(isUnpublishingSelector);

  const entertainmentVideos = useEntertainmentVideosForReview(
    pageType,
    offerId,
    deliverableId
  );

  const [isCancelConfirmationShown, setIsCancelConfirmationShown] =
    useState(false);
  const [currentPosition, setCurrentPosition] = useState<number>(() =>
    /**
     * Set current position to be the video that was clicked initially
     */
    entertainmentVideos.findIndex(
      fullEntertainmentVideo =>
        fullEntertainmentVideo.entertainmentVideo.id ===
        initialEntertainmentVideo.entertainmentVideo.id
    )
  );

  function getCurrentEntertainmentVideo() {
    const entertainmentVideo = entertainmentVideos[currentPosition];

    if (!entertainmentVideo) {
      /**
       * Shouldn't happen normally but left a check here just in case
       */
      throw new Error(
        'Current position is out of bounds for entertainment videos!'
      );
    }

    return entertainmentVideo;
  }

  const currentEntertainmentVideo = getCurrentEntertainmentVideo();

  return (
    <React.Fragment>
      <div className="entertainment-video-review__container">
        <EntertainmentVideoReviewHeader
          isEditMode={isEditMode}
          currentPosition={currentPosition}
          maxPosition={entertainmentVideos.length - 1}
          title={currentEntertainmentVideo.entertainmentVideo.name}
          subtitle={influencerProfile.name}
          onCurrentPositionChange={setCurrentPosition}
        />
        <div className="entertainment-video-review__info-container">
          <EntertainmentVideoReviewInfo
            fullEntertainmentVideo={currentEntertainmentVideo}
            isEditMode={isEditMode}
            onEditClick={() => {
              dispatch(setIsEditMode(true));
            }}
            onUnpublishClick={() => {
              if (isUnpublishing) {
                return;
              }

              dispatch(
                unpublishEntertainmentVideo({
                  pageType,
                  offerId,
                  deliverableId,
                  entertainmentVideoId: currentEntertainmentVideo.entertainmentVideo.id
                })
              );
            }}
          />
        </div>
        <div className="entertainment-video-review__details">
          {isEditMode ? (
            <EntertainmentVideoEditForm
              fullEntertainmentVideo={currentEntertainmentVideo}
              onCancel={isFormModified => {
                if (isFormModified) {
                  setIsCancelConfirmationShown(true);
                } else {
                  dispatch(setIsEditMode(false));
                }
              }}
              onSave={data => {
                if (isUpdating) {
                  return;
                }

                dispatch(
                  updateEntertainmentVideo({
                    pageType,
                    offerId,
                    deliverableId,
                    entertainmentVideoId:
                      currentEntertainmentVideo.entertainmentVideo.id,
                    data
                  })
                );
              }}
            />
          ) : (
            <EntertainmentVideoReviewDetails
              pageType={pageType}
              offerId={offerId}
              deliverableId={deliverableId}
              fullEntertainmentVideo={currentEntertainmentVideo}
            />
          )}
        </div>
      </div>
      <div className="entertainment-video-review__empty-container" />
      <ConfirmModal
        open={isCancelConfirmationShown}
        title={translate('.cancelConfirmationModal.title')}
        message={translate('.cancelConfirmationModal.message')}
        onCancel={() => {
          setIsCancelConfirmationShown(false);
        }}
        onConfirm={() => {
          setIsCancelConfirmationShown(false);
          dispatch(setIsEditMode(false));
        }}
      />
    </React.Fragment>
  );
}
